/**
 * Utility functions for generating article URLs based on content type
 * Handles backward compatibility and URL structure optimization
 */

import { Article } from '@/types/content';

export type ContentType = 'news' | 'guide' | 'analysis';

/**
 * Get the URL path prefix for a content type
 */
export function getContentTypePrefix(contentType?: ContentType): string {
  switch (contentType) {
    case 'guide':
      return 'guides';
    case 'analysis':
      return 'analysis';
    case 'news':
    default:
      return 'news'; // Default to news for backward compatibility
  }
}

/**
 * Get the full article path (e.g., /news/my-article-slug/)
 */
export function getArticlePath(article: Article | { slug: string; contentType?: ContentType }): string {
  const prefix = getContentTypePrefix(article.contentType);
  return `/${prefix}/${article.slug}/`;
}

/**
 * Get the absolute article URL
 */
export function getArticleAbsoluteUrl(
  article: Article | { slug: string; contentType?: ContentType },
  baseUrl?: string
): string {
  let siteUrl = baseUrl || process.env.NEXT_PUBLIC_SITE_URL || 'https://descout.xyz';
  
  // Ensure protocol is present
  if (!siteUrl.startsWith('http://') && !siteUrl.startsWith('https://')) {
    siteUrl = `https://${siteUrl}`;
  }
  
  // Remove trailing slash if present
  siteUrl = siteUrl.replace(/\/$/, '');
  
  return `${siteUrl}${getArticlePath(article)}`;
}

/**
 * Get content type display name for UI
 */
export function getContentTypeLabel(contentType?: ContentType): string {
  switch (contentType) {
    case 'guide':
      return 'Guide';
    case 'analysis':
      return 'Analysis';
    case 'news':
    default:
      return 'News';
  }
}

/**
 * Get content type description for UI
 */
export function getContentTypeDescription(contentType?: ContentType): string {
  switch (contentType) {
    case 'guide':
      return 'Tutorials, how-tos, and educational content';
    case 'analysis':
      return 'In-depth market analysis and research';
    case 'news':
    default:
      return 'Breaking news and timely updates';
  }
}

/**
 * Get suggested content type based on category tag name (for smart defaults)
 */
export function suggestContentType(categoryName?: string): ContentType {
  if (!categoryName) return 'news';
  
  const normalized = categoryName.toLowerCase();
  
  // Guide indicators
  if (
    normalized.includes('tutorial') ||
    normalized.includes('guide') ||
    normalized.includes('how to') ||
    normalized.includes('how-to') ||
    normalized.includes('education')
  ) {
    return 'guide';
  }
  
  // Analysis indicators
  if (
    normalized.includes('analysis') ||
    normalized.includes('research') ||
    normalized.includes('market') ||
    normalized.includes('report') ||
    normalized.includes('deep dive')
  ) {
    return 'analysis';
  }
  
  // Default to news
  return 'news';
}

/**
 * Get schema.org type for content type
 */
export function getSchemaType(contentType?: ContentType): string {
  switch (contentType) {
    case 'guide':
      return 'HowTo';
    case 'analysis':
      return 'Article';
    case 'news':
    default:
      return 'NewsArticle';
  }
}

/**
 * Get sitemap change frequency for content type
 */
export function getChangeFrequency(contentType?: ContentType): 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never' {
  switch (contentType) {
    case 'guide':
      return 'monthly'; // Guides are evergreen
    case 'analysis':
      return 'weekly'; // Analysis gets occasional updates
    case 'news':
    default:
      return 'daily'; // News is frequently updated
  }
}

/**
 * Get sitemap priority for content type based on recency
 */
export function getSitemapPriority(
  contentType: ContentType | undefined,
  daysSincePublished: number
): number {
  // Base priority by content type
  let basePriority: number;
  switch (contentType) {
    case 'guide':
      basePriority = 0.8; // Guides maintain high value
      break;
    case 'analysis':
      basePriority = 0.7;
      break;
    case 'news':
    default:
      basePriority = 0.6;
      break;
  }
  
  // Boost recent content
  if (daysSincePublished <= 7) {
    return Math.min(basePriority + 0.3, 1.0); // Recent boost
  } else if (daysSincePublished <= 30) {
    return Math.min(basePriority + 0.1, 1.0); // Small boost
  }

  return basePriority;
}

/** The drafting schema's seoTitle cap; a prefix of a longer headline that stops at the
 *  cap is a forced truncation, not a deliberate SEO rewrite. */
const SEO_TITLE_CAP = 60;

/**
 * Resolve the <title> / og:title / twitter:title for an article, honestly.
 *
 * The drafting schema caps `seoTitle` at ~60 chars and hard-truncates the headline to
 * fit — landing either mid-word ("…to Win the Anichess M", "…Led b", "…as Ev") or on a
 * word boundary that leaves the phrase dangling ("…Teases Pudgy Penguins" for
 * "…Teases Pudgy Penguins Integration"; "…Recovers 136 Transactions, Catches"). Both read
 * as broken in a browser tab and a shared social card, so when the seoTitle is just a
 * truncated prefix of a real headline that exceeds the cap we fall back to the full,
 * complete `title` — the honest value we already have. A genuinely distinct seoTitle
 * (not a mere prefix) is still preferred.
 *
 * NOTE: the root cause (the maxLength:60 slice in the breaking/article drafting schema)
 * is a publish-pipeline concern, out of the reader surface's scope; this is the
 * presentation-layer guard. See REVIEW-PACKET-ex-reader (REVIEW-REQUIRED finding).
 */
export function resolveArticleMetaTitle(
  seoTitle: string | null | undefined,
  title: string,
): string {
  const seo = (seoTitle ?? '').trim();
  const full = (title ?? '').trim();
  if (!seo) return full;
  return isHardTruncatedTitle(seo, full) ? full : seo;
}

/** True when `seo` is a cap-forced truncation of the real headline (`full`). */
function isHardTruncatedTitle(seo: string, full: string): boolean {
  // Primary signal: `seo` is a proper prefix of a headline that overruns the cap. That is
  // exactly what truncating `title` to fit ~60 chars produces (mid-word OR at a word
  // boundary). A headline that fits the cap would not need a separate truncated seoTitle,
  // so a shorter prefix of a >cap headline is a forced chop, not an editorial choice.
  if (full.length > SEO_TITLE_CAP && full.length > seo.length && full.startsWith(seo)) {
    return true;
  }
  // Backstop for a distinct (non-prefix) seoTitle that was itself sliced mid-word near
  // the cap, leaving a 1–2 char stub ("… M", "… Ev", "… b").
  if (seo.length >= SEO_TITLE_CAP - 4 && /[A-Za-z0-9]$/.test(seo)) {
    const lastToken = seo.split(/\s+/).pop() ?? '';
    if (lastToken.length <= 2) return true;
  }
  return false;
}

